/**
 * pointsService.ts
 * 
 * Shared point and experience mutation service using Optimistic Concurrency Control (OCC).
 * Extracted to break the circular dependency between supabaseService and gamificationService.
 */

import { db as supabase } from '../lib/dbClient';
import { MonetaryEngine } from '../../api/utils/monetaryEngine';

export const pointsService = {
  /**
   * Registra uma entrada de CRÉDITO em `usuario_pontos` com o valor em reais por ponto
   * já fixado no momento da concessão (origem + valor_unitario + valor_total).
   *
   * Isso é o que permite, no resgate de cashback, saber quanto cada ponto vale de
   * acordo com sua origem (pontos de check-in/missão/quiz valem R$0,01; pontos de
   * descarte valem o preço do material no momento em que foi descartado) — em vez de
   * aplicar uma taxa fixa igual para todo mundo. Chame isso sempre que creditar pontos
   * por qualquer fonte (além de `atomicAddUserPoints`, que só mantém o contador
   * `usuarios.pontos` — este método aqui é o que registra a origem e o valor).
   *
   * Não lança erro: se a gravação do ledger falhar, os pontos já creditados via
   * `atomicAddUserPoints` continuam valendo (não revertemos por uma falha secundária
   * de bookkeeping), mas o evento fica sem valor de cashback rastreável — por isso o
   * erro é logado com destaque, não apenas engolido silenciosamente.
   */
  async recordPointsLedgerEntry(params: {
    userId: string;
    pontos: number;
    origem: string;
    valorUnitario: number;
    referenciaId?: string | null;
  }): Promise<void> {
    if (!params.pontos || params.pontos <= 0) return;
    const valorUnitario = Number(params.valorUnitario) || 0;
    const valorTotal = MonetaryEngine.multiply(valorUnitario, params.pontos);

    const { data: lote, error } = await supabase.from('usuario_pontos').insert([{
      usuario_id: params.userId,
      origem: params.origem,
      tipo_ponto: 'CREDITO',
      quantidade_pontos: params.pontos,
      peso_financeiro: 0,
      valor_unitario: valorUnitario,
      valor_total: valorTotal,
      saldo_restante: params.pontos,
      status: 'CONSOLIDADO',
      referencia_id: params.referenciaId || null
    }]).select('id').single();

    if (error) {
      console.error(`[pointsService] FALHA ao registrar ledger de valor por origem (origem=${params.origem}, userId=${params.userId}, pontos=${params.pontos}) — pontos já foram creditados normalmente, mas este evento não terá valor de cashback rastreável:`, error);
      return;
    }
    await this.quitarDividas(params.userId, lote.id, params.pontos);
  },

  /**
   * Saldo negativo: quando um descarte é estornado depois que o usuário já resgatou os pontos,
   * a diferença fica como lançamento DEBITO com status PENDENTE (saldo_restante = pontos devidos).
   * Todo crédito novo quita primeiro essa dívida (FIFO) e só o que sobrar fica resgatável.
   * Retorna quantos pontos do lote foram usados para quitar.
   */
  async quitarDividas(userId: string, loteId: string, pontosLote: number): Promise<number> {
    const { data: dividas } = await supabase
      .from('usuario_pontos')
      .select('id, saldo_restante')
      .eq('usuario_id', userId)
      .eq('tipo_ponto', 'DEBITO')
      .eq('status', 'PENDENTE')
      .gt('saldo_restante', 0)
      .order('created_at', { ascending: true });
    let disponivel = pontosLote;
    for (const d of dividas || []) {
      if (disponivel <= 0) break;
      const usar = Math.min(disponivel, Number(d.saldo_restante));
      const resta = Number(d.saldo_restante) - usar;
      await supabase.from('usuario_pontos')
        .update({ saldo_restante: resta, status: resta > 0 ? 'PENDENTE' : 'CONSOLIDADO', updated_at: new Date().toISOString() })
        .eq('id', d.id);
      disponivel -= usar;
    }
    const usado = pontosLote - disponivel;
    if (usado > 0) {
      await supabase.from('usuario_pontos').update({ saldo_restante: disponivel, updated_at: new Date().toISOString() }).eq('id', loteId);
    }
    return usado;
  },

  /**
   * Atomically adds points to a user using Optimistic Concurrency Control (OCC).
   * Prevents lost updates under concurrent transactions.
   */
  async atomicAddUserPoints(userId: string, pointsToAdd: number, maxRetries = 5): Promise<number> {
    if (pointsToAdd <= 0) return 0;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const { data: user, error } = await supabase
        .from('usuarios')
        .select('pontos, saldo_pontos')
        .eq('id', userId)
        .single();

      if (error || !user) throw new Error('Usuário não encontrado ao creditar pontos');

      const currentPoints = Number(user.pontos || 0);
      const currentSaldo = Number(user.saldo_pontos || currentPoints);
      const newPoints = currentPoints + pointsToAdd;
      const newSaldo = currentSaldo + pointsToAdd;

      const { data: updated, error: updateError } = await supabase
        .from('usuarios')
        .update({ 
          pontos: newPoints, 
          saldo_pontos: newSaldo,
          data_atualizacao: new Date().toISOString() 
        })
        .eq('id', userId)
        .eq('pontos', currentPoints) // OCC guard
        .select('id, pontos')
        .maybeSingle();

      if (!updateError && updated) {
        return updated.pontos;
      }
      await new Promise(res => setTimeout(res, 20 + Math.random() * 50));
    }
    throw new Error('Falha de concorrência ao atualizar pontos do usuário');
  },

  /**
   * Atomically deducts points from a user using Optimistic Concurrency Control (OCC).
   * Prevents double-spending under concurrent redemptions.
   */
  async atomicDeductUserPoints(userId: string, pointsToDeduct: number, maxRetries = 5): Promise<number> {
    if (pointsToDeduct <= 0) return 0;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const { data: user, error } = await supabase
        .from('usuarios')
        .select('pontos, saldo_pontos')
        .eq('id', userId)
        .single();

      if (error || !user) throw new Error('Usuário não encontrado ao debitar pontos');

      const currentPoints = Number(user.pontos || 0);
      if (currentPoints < pointsToDeduct) {
        throw new Error('Pontos insuficientes');
      }

      const currentSaldo = Number(user.saldo_pontos || currentPoints);
      const newPoints = currentPoints - pointsToDeduct;
      const newSaldo = Math.max(0, currentSaldo - pointsToDeduct);

      const { data: updated, error: updateError } = await supabase
        .from('usuarios')
        .update({ 
          pontos: newPoints, 
          saldo_pontos: newSaldo,
          data_atualizacao: new Date().toISOString() 
        })
        .eq('id', userId)
        .eq('pontos', currentPoints) // OCC guard
        .select('id, pontos')
        .maybeSingle();

      if (!updateError && updated) {
        return updated.pontos;
      }
      await new Promise(res => setTimeout(res, 20 + Math.random() * 50));
    }
    throw new Error('Falha de concorrência ao debitar pontos do usuário');
  },

  /**
   * Updates user experience (XP) across leagues and periodic rankings.
   */
  async updateUserXP(userId: string, xpAmount: number) {
    if (xpAmount <= 0) return;

    // 1. Get current user league status
    let { data: userLeague, error: fetchError } = await supabase
      .from('usuarios_ligas')
      .select('*, ligas(*)')
      .eq('usuario_id', userId)
      .maybeSingle();
    
    if (fetchError) throw fetchError;

    // 2. If user doesn't have a league, assign the first one (level 1)
    if (!userLeague) {
      const { data: allLeagues } = await supabase
        .from('ligas')
        .select('*')
        .order('nivel', { ascending: true });
      
      const firstLeague = allLeagues?.[0];
      
      if (firstLeague) {
        const { data: newUserLeague, error: insertError } = await supabase
          .from('usuarios_ligas')
          .insert([{
            usuario_id: userId,
            liga_id: firstLeague.id,
            xp_total: xpAmount
          }])
          .select('*, ligas(*)')
          .single();
        
        if (insertError) throw insertError;
        userLeague = newUserLeague;

        // Record history
        await supabase
          .from('usuarios_ligas_historico')
          .insert([{
            usuario_id: userId,
            liga_nova_id: firstLeague.id,
            tipo: 'ENTRADA'
          }]);
      } else {
        console.warn('No leagues defined in the system.');
        return;
      }
    } else {
      // Update Total XP with OCC guard
      const curXp = Number(userLeague.xp_total || 0);
      const { data: updatedXp, error: updateXpError } = await supabase
        .from('usuarios_ligas')
        .update({ 
          xp_total: curXp + xpAmount,
          data_atualizacao: new Date().toISOString()
        })
        .eq('id', userLeague.id)
        .eq('xp_total', curXp)
        .select()
        .maybeSingle();
      
      if (!updatedXp && !updateXpError) {
        const { data: fresh } = await supabase.from('usuarios_ligas').select('xp_total').eq('id', userLeague.id).single();
        if (fresh) {
          await supabase.from('usuarios_ligas').update({ xp_total: Number(fresh.xp_total || 0) + xpAmount }).eq('id', userLeague.id);
        }
      }
    }

    // 3. Update periodic ranking (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    
    const { data: currentPeriod } = await supabase
      .from('periodos_ranking')
      .select('id')
      .lte('data_inicio', now.toISOString())
      .gte('data_fim', now.toISOString())
      .maybeSingle();
    
    let periodId = currentPeriod?.id;

    if (!periodId) {
      const { data: newPeriod, error: pError } = await supabase
        .from('periodos_ranking')
        .insert([{
          nome: `Temporada ${now.getMonth() + 1}/${now.getFullYear()}`,
          data_inicio: firstDay.toISOString(),
          data_fim: lastDay.toISOString(),
          status: 'ATIVO'
        }])
        .select('id')
        .single();
      
      if (!pError && newPeriod) {
        periodId = newPeriod.id;
      }
    }

    if (periodId && userLeague) {
      const { data: existingRanking } = await supabase
        .from('ranking_ligas')
        .select('id, xp_periodo')
        .eq('usuario_id', userId)
        .eq('periodo_id', periodId)
        .maybeSingle();

      if (existingRanking) {
        await supabase
          .from('ranking_ligas')
          .update({
            xp_periodo: Number(existingRanking.xp_periodo || 0) + xpAmount,
            data_atualizacao: new Date().toISOString()
          })
          .eq('id', existingRanking.id);
      } else {
        await supabase
          .from('ranking_ligas')
          .insert([{
            usuario_id: userId,
            liga_id: userLeague.liga_id,
            periodo_id: periodId,
            xp_periodo: xpAmount,
            posicao: 9999
          }]);
      }
    }
  }
};
